import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Circle, Loader2, X } from 'lucide-react'
import { Field, NativeSelect } from '@/components/common/Field'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useAgents, useContacts, useCustomers, useProducts } from '@/hooks/useDirectory'
import { useSubmitManualTicket, type StepProgress } from '@/hooks/useManualTicket'
import { useApplicableSlaPolicy } from '@/hooks/useTickets'
import {
  LIMITS,
  MANUAL_GXP_IMPACTS,
  RECEIVED_VIA,
  checkRecordKey,
  defaultTenantId,
  emptyManualTicket,
  followUpSteps,
  formatBudget,
  keyExample,
  noteMentions,
  toCreatePayload,
  toLocalInput,
  validateManualTicket,
  type ManualTicketErrors,
  type ManualTicketField,
  type ManualTicketForm,
  type ManualTicketMode,
} from '@/lib/manualTicket'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { FILTER_PRIORITIES, FILTER_TYPES } from '@/lib/queueFilters'
import { formatBytes, viewerTimeZone } from '@/lib/threadView'
import { priorityLabel, typeLabel } from '@/lib/ticketLabels'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'

const OTHER_TYPE = '__other__'

function Section({ title, children, tone }: { title: string; children: ReactNode; tone?: 'note' }) {
  return (
    <section
      aria-label={title}
      className={cn('flex flex-col gap-4 rounded-lg border p-5', tone === 'note' ? 'border-note-border bg-note-bg/40' : 'border-line')}
    >
      <h2 className={cn('text-eyebrow font-medium uppercase', tone === 'note' ? 'text-note-heading' : 'text-ink-muted')}>{title}</h2>
      {children}
    </section>
  )
}

/** Two-option segmented control (reporter mode). */
function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex w-fit rounded-md border border-line-strong p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-[4px] px-3 py-1.5 text-meta',
            value === o.value ? 'bg-ink font-medium text-white' : 'text-ink-body hover:bg-surface-subtle',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function StepIcon({ state }: { state: StepProgress['state'] }) {
  if (state === 'done') return <Check aria-hidden className="size-4 text-resolved-heading" />
  if (state === 'failed') return <X aria-hidden className="size-4 text-status-escalated-fg" />
  if (state === 'running') return <Loader2 aria-hidden className="size-4 animate-spin text-ink-muted" />
  return <Circle aria-hidden className="size-4 text-marker-muted" />
}

/**
 * Full-page ticket intake (`/tickets/new`). Agents log a request that arrived
 * by phone, email, Teams or in person — customer, reporter (existing contact
 * or a new one), intake channel and time, GxP assessment, linked record,
 * assignee, watchers, attachments and an opening internal note. Customers get
 * the reporter's subset. Submits as create → follow-up steps, with progress;
 * attachments that fail are retried from the ticket page.
 */
export default function NewTicketPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const mode: ManualTicketMode = user?.kind === 'CUSTOMER' ? 'customer' : 'agent'
  const isAgent = mode === 'agent'
  const timeZone = useMemo(() => viewerTimeZone(), [])

  const can = {
    create: usePermission(PERMISSIONS.ticketsCreate),
    assign: usePermission(PERMISSIONS.ticketsAssign),
    notes: usePermission(PERMISSIONS.notesWrite),
    upload: usePermission(PERMISSIONS.attachmentsUpload),
  }

  const [form, setForm] = useState<ManualTicketForm>(() => emptyManualTicket(toLocalInput(new Date())))
  const [touched, setTouched] = useState<Partial<Record<ManualTicketField, boolean>>>({})
  const [submitted, setSubmitted] = useState(false)
  const [files, setFiles] = useState<{ file: File; internal: boolean }[]>([])
  const [customType, setCustomType] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const run = useSubmitManualTicket()
  const [outcome, setOutcome] = useState<{
    key: string
    createError: string | null
    partial: boolean
  } | null>(null)

  const customers = useCustomers()
  const products = useProducts()
  const agents = useAgents(isAgent)
  const customerId = isAgent ? form.customerId : (user?.customerId ?? customers.data?.[0]?.id ?? '')
  const contacts = useContacts(customerId || null)

  const customer = customers.data?.find((c) => c.id === customerId)
  const tenants = customer?.tenants ?? []
  const reporterContact = form.reporterMode === 'existing' ? contacts.data?.find((c) => c.id === form.contactId) : undefined
  const reporterTenant = tenants.find((x) => x.id === reporterContact?.tenantId)
  const product = products.data?.find((p) => p.id === form.productId)
  const environments = useMemo(
    () => (customer?.environments ?? []).filter((e) => !form.productId || e.productId === form.productId),
    [customer, form.productId],
  )
  const keyPatterns = product?.keyPatterns ?? []
  const pattern = keyPatterns.find((p) => p.type === form.recordType.trim())
  const keyCheck = checkRecordKey(form.recordKey, pattern)

  const errors: ManualTicketErrors = validateManualTicket(form, mode, {
    keyPatterns,
  })
  const shown = (field: ManualTicketField) => ((submitted || touched[field]) && errors[field] ? t(`manualTicket.errors.${errors[field]}`) : undefined)

  const set = <K extends keyof ManualTicketForm>(key: K, value: ManualTicketForm[K]) => setForm((f) => ({ ...f, [key]: value }))
  const touch = (field: ManualTicketField) => () => setTouched((x) => ({ ...x, [field]: true }))

  const steps = followUpSteps(form, mode, {
    files: can.upload ? files.length : 0,
    canWriteNotes: can.notes,
  })

  // The same resolution the API's SlaService uses (GET /sla-policies/applicable).
  const policy =
    useApplicableSlaPolicy({ priority: form.priority, customerId, productId: form.productId }, isAgent && can.create).data ?? null
  const slaHint = policy
    ? t('manualTicket.slaHint', {
        policy: policy.name,
        fr: formatBudget(policy.firstResponseMins),
        res: formatBudget(policy.resolutionMins),
        calendar: policy.calendar?.name
          ? t('manualTicket.slaHintCalendar', {
              calendar: policy.calendar.name,
            })
          : '',
      })
    : t('manualTicket.slaHintDefault')

  const watcherOptions = (contacts.data ?? []).filter((c) => c.id !== form.contactId)
  const busy = run.running

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault()
    setSubmitted(true)
    if (Object.keys(errors).length > 0 || busy) return
    setOutcome(null)
    const payload = toCreatePayload(form, mode, {
      productKey: product?.key,
      canAssign: can.assign,
    })
    const result = await run.submit({
      payload,
      steps,
      files: files.map((f) => ({
        file: f.file,
        visibility: isAgent && f.internal ? 'internal' : 'customer',
      })),
      note: {
        body: form.internalNote.trim(),
        mentions: noteMentions(form.internalNote, agents.data ?? []),
      },
    })
    if (!result.ticket) {
      setOutcome({ key: '', createError: result.createError, partial: false })
      return
    }
    // Attachment failures are retried on the ticket page; anything else keeps the summary here.
    if (result.failed.length === 0) {
      navigate(`/tickets/${result.ticket.key}`)
      return
    }
    setOutcome({ key: result.ticket.key, createError: null, partial: true })
  }

  const title = isAgent ? t('manualTicket.titleAgent') : t('manualTicket.titleCustomer')
  const eyebrow = isAgent ? t('manualTicket.eyebrowAgent') : t('manualTicket.eyebrowCustomer', { customer: customer?.name ?? '' })

  return (
    <Page>
      <PageHeading
        description={eyebrow}
        title={title}
        actions={
          <Link to="/queue" className="text-meta font-medium text-ink-muted hover:text-ink hover:underline">
            {t('manualTicket.backToQueue')}
          </Link>
        }
      />
      <p className="-mt-3 max-w-[720px] text-body text-ink-body">{isAgent ? t('manualTicket.introAgent') : t('manualTicket.introCustomer')}</p>

      <form onSubmit={(e) => void onSubmit(e)} noValidate className="grid grid-cols-[minmax(0,1fr)_320px] items-start gap-8">
        <fieldset disabled={busy} className="flex min-w-0 flex-col gap-5">
          {isAgent ? (
            <Section title={t('manualTicket.sections.reporter')}>
              <div className="grid grid-cols-2 gap-4">
                <Field id="mt-customer" label={t('manualTicket.customer')} error={shown('customerId')}>
                  <NativeSelect
                    id="mt-customer"
                    value={form.customerId}
                    onBlur={touch('customerId')}
                    aria-invalid={!!shown('customerId')}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        customerId: e.target.value,
                        environmentId: '',
                        tenantId: '',
                        contactId: '',
                        watcherContactIds: [],
                      }))
                    }
                  >
                    <option value="">{t('common.select')}</option>
                    {customers.data?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <div className="flex flex-col gap-1.5">
                  <span className="text-meta font-medium text-ink">{t('manualTicket.reporterMode')}</span>
                  <Segmented
                    label={t('manualTicket.reporterMode')}
                    value={form.reporterMode}
                    onChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        reporterMode: v,
                        tenantId: v === 'existing' ? defaultTenantId(contacts.data?.find((c) => c.id === f.contactId), tenants) : '',
                      }))
                    }
                    options={[
                      {
                        value: 'existing',
                        label: t('manualTicket.existingContact'),
                      },
                      { value: 'new', label: t('manualTicket.newContact') },
                    ]}
                  />
                </div>
              </div>
              {form.reporterMode === 'existing' ? (
                <Field id="mt-contact" label={t('manualTicket.contact')} error={shown('contactId')}>
                  <NativeSelect
                    id="mt-contact"
                    value={form.contactId}
                    disabled={!form.customerId}
                    onBlur={touch('contactId')}
                    aria-invalid={!!shown('contactId')}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        contactId: e.target.value,
                        // Default the tenant to the reporter's own branch.
                        tenantId: defaultTenantId(contacts.data?.find((c) => c.id === e.target.value), tenants),
                        watcherContactIds: f.watcherContactIds.filter((w) => w !== e.target.value),
                      }))
                    }
                  >
                    <option value="">{form.customerId ? t('common.select') : t('manualTicket.pickCustomerFirst')}</option>
                    {contacts.data?.map((c) => (
                      <option key={c.id} value={c.id}>
                        {[c.name, c.role, c.email].filter(Boolean).join(' · ')}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <Field id="mt-cname" label={t('manualTicket.contactName')} error={shown('newContactName')}>
                    <Input
                      id="mt-cname"
                      value={form.newContactName}
                      maxLength={LIMITS.name}
                      autoComplete="off"
                      onBlur={touch('newContactName')}
                      onChange={(e) => set('newContactName', e.target.value)}
                    />
                  </Field>
                  <Field id="mt-cemail" label={t('manualTicket.contactEmail')} error={shown('newContactEmail')}>
                    <Input
                      id="mt-cemail"
                      type="email"
                      value={form.newContactEmail}
                      autoComplete="off"
                      onBlur={touch('newContactEmail')}
                      onChange={(e) => set('newContactEmail', e.target.value)}
                    />
                  </Field>
                  <Field
                    id="mt-crole"
                    label={`${t('manualTicket.contactRole')} (${t('common.optional')})`}
                    help={t('manualTicket.newContactRoleNote')}
                  >
                    <Input
                      id="mt-crole"
                      value={form.newContactRole}
                      maxLength={LIMITS.name}
                      placeholder={t('manualTicket.contactRolePlaceholder')}
                      onChange={(e) => set('newContactRole', e.target.value)}
                    />
                  </Field>
                  <p className="col-span-3 -mt-2 text-label text-ink-muted">
                    {t('manualTicket.newContactHelp', {
                      customer: customer?.name ?? t('manualTicket.customer'),
                    })}
                  </p>
                </div>
              )}
              <Field
                id="mt-tenant"
                label={t('manualTicket.tenant')}
                className="max-w-[360px]"
                help={customer && tenants.length === 0 ? t('manualTicket.noTenants') : t('manualTicket.tenantHelp')}
              >
                <NativeSelect
                  id="mt-tenant"
                  value={form.tenantId}
                  disabled={!customer || tenants.length === 0}
                  onChange={(e) => set('tenantId', e.target.value)}
                >
                  <option value="">
                    {reporterTenant
                      ? t('manualTicket.tenantFromReporterNamed', { tenant: reporterTenant.name })
                      : t('manualTicket.tenantFromReporter')}
                  </option>
                  {tenants.map((x) => (
                    <option key={x.id} value={x.id}>
                      {`${x.name} (${x.code})`}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </Section>
          ) : null}

          {isAgent ? (
            <Section title={t('manualTicket.sections.intake')}>
              <div className="grid grid-cols-2 gap-4">
                <Field id="mt-via" label={t('manualTicket.receivedVia')}>
                  <NativeSelect
                    id="mt-via"
                    value={form.receivedVia}
                    onChange={(e) => set('receivedVia', e.target.value as ManualTicketForm['receivedVia'])}
                  >
                    {RECEIVED_VIA.map((v) => (
                      <option key={v} value={v}>
                        {t(`manualTicket.via.${v}`)}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field
                  id="mt-at"
                  label={t('manualTicket.receivedAt')}
                  error={shown('receivedAt')}
                  help={t('manualTicket.receivedAtHelp', { zone: timeZone })}
                >
                  <Input
                    id="mt-at"
                    type="datetime-local"
                    value={form.receivedAt}
                    max={toLocalInput(new Date())}
                    onBlur={touch('receivedAt')}
                    onChange={(e) => set('receivedAt', e.target.value)}
                  />
                </Field>
              </div>
            </Section>
          ) : null}

          <Section title={t('manualTicket.sections.about')}>
            <div className="grid grid-cols-3 gap-4">
              <Field id="mt-product" label={t('manualTicket.product')} error={shown('productId')}>
                <NativeSelect
                  id="mt-product"
                  value={form.productId}
                  onBlur={touch('productId')}
                  aria-invalid={!!shown('productId')}
                  onChange={(e) => {
                    setCustomType(false)
                    setForm((f) => ({
                      ...f,
                      productId: e.target.value,
                      componentId: '',
                      environmentId: '',
                      recordType: '',
                      recordKey: '',
                    }))
                  }}
                >
                  <option value="">{t('common.select')}</option>
                  {products.data
                    ?.filter((p) => p.active !== false)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </NativeSelect>
              </Field>
              <Field id="mt-component" label={t('manualTicket.component')}>
                <NativeSelect id="mt-component" value={form.componentId} disabled={!product} onChange={(e) => set('componentId', e.target.value)}>
                  <option value="">{product ? t('manualTicket.noComponent') : t('manualTicket.pickProductFirst')}</option>
                  {product?.components.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field id="mt-env" label={t('manualTicket.environment')}>
                <NativeSelect id="mt-env" value={form.environmentId} disabled={!customer} onChange={(e) => set('environmentId', e.target.value)}>
                  <option value="">{customer ? t('manualTicket.noEnvironment') : t('manualTicket.pickCustomerFirst')}</option>
                  {environments.map((env) => (
                    <option key={env.id} value={env.id}>
                      {[env.product?.name, env.name].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field id="mt-type" label={t('manualTicket.type')}>
                <NativeSelect id="mt-type" value={form.type} onChange={(e) => set('type', e.target.value)}>
                  {FILTER_TYPES.map((v) => (
                    <option key={v} value={v}>
                      {typeLabel(v)}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field id="mt-priority" label={t('manualTicket.priority')} className="col-span-2" help={isAgent ? slaHint : undefined}>
                <NativeSelect id="mt-priority" value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                  {FILTER_PRIORITIES.map((v) => (
                    <option key={v} value={v}>
                      {priorityLabel(v)}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </div>
            <Field id="mt-subject" label={t('manualTicket.subject')} error={shown('subject')}>
              <Input
                id="mt-subject"
                value={form.subject}
                maxLength={LIMITS.subject}
                placeholder={t('manualTicket.subjectPlaceholder')}
                onBlur={touch('subject')}
                onChange={(e) => set('subject', e.target.value)}
              />
            </Field>
            <Field id="mt-description" label={t('manualTicket.description')} error={shown('description')}>
              <Textarea
                id="mt-description"
                rows={8}
                value={form.description}
                placeholder={isAgent ? t('manualTicket.descriptionPlaceholderAgent') : t('manualTicket.descriptionPlaceholderCustomer')}
                onBlur={touch('description')}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>
          </Section>

          <Section title={t('manualTicket.sections.gxp')}>
            {isAgent ? (
              <div className="grid grid-cols-3 gap-4">
                <Field id="mt-gxp" label={t('manualTicket.gxpImpact')}>
                  <NativeSelect id="mt-gxp" value={form.gxpImpact} onChange={(e) => set('gxpImpact', e.target.value)}>
                    {MANUAL_GXP_IMPACTS.map((v) => (
                      <option key={v} value={v}>
                        {t(`manualTicket.gxp.${v}`)}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                {form.gxpImpact !== 'unassessed' ? (
                  <Field id="mt-gxpnote" label={t('manualTicket.gxpNote')} error={shown('gxpNote')} className="col-span-2">
                    <Textarea
                      id="mt-gxpnote"
                      rows={2}
                      value={form.gxpNote}
                      maxLength={LIMITS.gxpNote}
                      placeholder={t('manualTicket.gxpNotePlaceholder')}
                      onBlur={touch('gxpNote')}
                      onChange={(e) => set('gxpNote', e.target.value)}
                    />
                  </Field>
                ) : null}
              </div>
            ) : null}
            <div className="grid grid-cols-3 gap-4">
              <Field id="mt-formref" label={`${t('manualTicket.formRef')} (${t('common.optional')})`}>
                <Input
                  id="mt-formref"
                  className="font-mono"
                  value={form.formRef}
                  maxLength={LIMITS.formRef}
                  placeholder={t('manualTicket.formRefPlaceholder')}
                  onChange={(e) => set('formRef', e.target.value)}
                />
              </Field>
              <Field id="mt-formver" label={t('manualTicket.formVersion')} error={shown('formVersion')}>
                <Input
                  id="mt-formver"
                  className="font-mono"
                  value={form.formVersion}
                  maxLength={LIMITS.formVersion}
                  placeholder={t('manualTicket.formVersionPlaceholder')}
                  onBlur={touch('formVersion')}
                  onChange={(e) => set('formVersion', e.target.value)}
                />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field id="mt-rtype" label={t('manualTicket.recordType')} error={shown('recordType')}>
                <NativeSelect
                  id="mt-rtype"
                  value={customType ? OTHER_TYPE : form.recordType}
                  disabled={!product}
                  onChange={(e) => {
                    const v = e.target.value
                    setCustomType(v === OTHER_TYPE)
                    setForm((f) => ({
                      ...f,
                      recordType: v === OTHER_TYPE ? '' : v,
                      recordKey: v ? f.recordKey : '',
                    }))
                  }}
                >
                  <option value="">{product ? t('manualTicket.recordTypeNone') : t('manualTicket.pickProductFirst')}</option>
                  {keyPatterns.map((p) => (
                    <option key={p.type} value={p.type}>
                      {p.type.toUpperCase()}
                    </option>
                  ))}
                  <option value={OTHER_TYPE}>{t('manualTicket.recordTypeOther')}</option>
                </NativeSelect>
              </Field>
              {customType ? (
                <Field id="mt-rtype-custom" label={t('manualTicket.recordTypeCustom')}>
                  <Input id="mt-rtype-custom" value={form.recordType} maxLength={50} onChange={(e) => set('recordType', e.target.value)} />
                </Field>
              ) : null}
              {form.recordType || customType ? (
                <Field
                  id="mt-rkey"
                  label={t('manualTicket.recordKey')}
                  className={customType ? '' : 'col-span-2'}
                  error={
                    keyCheck === 'invalid'
                      ? t('manualTicket.recordKeyInvalid', {
                          type: form.recordType.toUpperCase(),
                          example: pattern ? keyExample(pattern) : '',
                        })
                      : shown('recordKey')
                  }
                  help={
                    keyCheck === 'valid'
                      ? t('manualTicket.recordKeyValid', {
                          type: form.recordType.toUpperCase(),
                        })
                      : keyCheck === 'unchecked'
                        ? t('manualTicket.recordKeyUnchecked')
                        : undefined
                  }
                >
                  <Input
                    id="mt-rkey"
                    className={cn(
                      'font-mono',
                      keyCheck === 'valid' && 'border-resolved-border',
                      keyCheck === 'invalid' && 'border-status-escalated-fg',
                    )}
                    value={form.recordKey}
                    maxLength={200}
                    aria-invalid={keyCheck === 'invalid'}
                    onBlur={touch('recordKey')}
                    onChange={(e) => set('recordKey', e.target.value)}
                  />
                </Field>
              ) : null}
            </div>
          </Section>

          <Section title={t('manualTicket.sections.people')}>
            {isAgent && can.assign ? (
              <Field id="mt-assignee" label={t('manualTicket.assignee')} className="max-w-[360px]">
                <NativeSelect id="mt-assignee" value={form.assigneeId} onChange={(e) => set('assigneeId', e.target.value)}>
                  <option value="">{t('manualTicket.unassigned')}</option>
                  {agents.data?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {[a.name, a.title ?? a.team].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            ) : null}
            <fieldset className="flex flex-col gap-2">
              <legend className="pb-1.5 text-meta font-medium text-ink">{t('manualTicket.watchers')}</legend>
              {!customerId ? (
                <p className="text-label text-ink-muted">{t('manualTicket.pickCustomerFirst')}</p>
              ) : watcherOptions.length === 0 ? (
                <p className="text-label text-ink-muted">{t('manualTicket.noWatchers')}</p>
              ) : (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {watcherOptions.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-meta text-ink">
                      <input
                        type="checkbox"
                        checked={form.watcherContactIds.includes(c.id)}
                        onChange={(e) =>
                          set(
                            'watcherContactIds',
                            e.target.checked ? [...form.watcherContactIds, c.id] : form.watcherContactIds.filter((w) => w !== c.id),
                          )
                        }
                      />
                      <span className="truncate">
                        {c.name}
                        {c.role ? <span className="text-ink-muted"> · {c.role}</span> : null}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-label text-ink-muted">{t('manualTicket.watchersHelp')}</p>
            </fieldset>
          </Section>

          {can.upload ? (
            <Section title={t('manualTicket.sections.attachments')}>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-md border border-line-strong bg-background px-3.5 py-2 text-meta font-medium text-ink hover:bg-surface-subtle"
                >
                  {t('manualTicket.addFiles')}
                </button>
                <span className="text-label text-ink-muted">{t('manualTicket.attachmentsHelp')}</span>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  hidden
                  data-testid="mt-files"
                  onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []).map((file) => ({ file, internal: false }))
                    setFiles((all) => [...all, ...picked])
                    e.target.value = ''
                  }}
                />
              </div>
              {files.length > 0 ? (
                <ul className="flex flex-col">
                  {files.map((f, i) => (
                    <li key={`${f.file.name}-${i}`} className="flex items-center gap-3 border-b border-line py-2 last:border-b-0">
                      <span className="min-w-0 grow truncate text-body text-ink-strong">{f.file.name}</span>
                      <span className="w-16 shrink-0 text-right text-label text-ink-faint">{formatBytes(f.file.size)}</span>
                      {isAgent ? (
                        <label className="flex shrink-0 items-center gap-1.5 text-label text-ink-body">
                          <input
                            type="checkbox"
                            checked={f.internal}
                            onChange={(e) => setFiles((all) => all.map((x, j) => (j === i ? { ...x, internal: e.target.checked } : x)))}
                          />
                          {t('manualTicket.internalOnly')}
                        </label>
                      ) : null}
                      <button
                        type="button"
                        aria-label={t('manualTicket.removeFile', {
                          name: f.file.name,
                        })}
                        onClick={() => setFiles((all) => all.filter((_, j) => j !== i))}
                        className="shrink-0 rounded-md p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink"
                      >
                        <X className="size-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Section>
          ) : null}

          {isAgent && can.notes ? (
            <Section title={t('manualTicket.sections.note')} tone="note">
              <Field id="mt-note" label={t('manualTicket.note')} help={t('manualTicket.noteHelp')}>
                <Textarea
                  id="mt-note"
                  rows={3}
                  value={form.internalNote}
                  className="bg-background"
                  placeholder={t('manualTicket.notePlaceholder')}
                  onChange={(e) => set('internalNote', e.target.value)}
                />
              </Field>
            </Section>
          ) : null}
        </fieldset>

        <aside className="sticky top-6 flex flex-col gap-4 rounded-lg border border-line p-5" aria-label={t('manualTicket.summary')}>
          <h2 className="text-eyebrow font-medium uppercase text-ink-muted">
            {run.progress.length ? t('manualTicket.progressTitle') : t('manualTicket.summary')}
          </h2>
          <ol className="flex flex-col gap-2.5">
            {(run.progress.length
              ? run.progress
              : (['create', ...steps] as StepProgress['key'][]).map((key) => ({
                  key,
                  state: 'pending' as const,
                }))
            ).map((p: StepProgress) => (
              <li key={p.key} className="flex items-start gap-2.5" data-state={p.state}>
                <span className="pt-px">
                  <StepIcon state={p.state} />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className={cn('text-meta', p.state === 'failed' ? 'text-status-escalated-fg' : 'text-ink')}>
                    {t(`manualTicket.willRun.${p.key}`, {
                      count: files.length,
                    })}
                  </span>
                  {run.progress.length ? (
                    <span className="truncate text-label text-ink-muted">
                      {p.detail ? `${t(`manualTicket.step.${p.state}`)} · ${p.detail}` : t(`manualTicket.step.${p.state}`)}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>

          {submitted && Object.keys(errors).length > 0 ? (
            <p role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {t('manualTicket.validationSummary')}
            </p>
          ) : null}
          {outcome?.createError ? (
            <p role="alert" className="rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {t('manualTicket.createFailed', { message: outcome.createError })}
            </p>
          ) : null}
          {outcome?.partial ? (
            <div role="alert" className="flex flex-col gap-2 rounded-md border border-note-border bg-note-bg px-3 py-2">
              <p className="text-meta text-note-text">{t('manualTicket.partial', { key: outcome.key })}</p>
              <Link to={`/tickets/${outcome.key}`} className="text-meta font-semibold text-note-heading hover:underline">
                {t('manualTicket.openTicket', { key: outcome.key })}
              </Link>
            </div>
          ) : null}

          {outcome?.partial ? null : (
            <div className="flex flex-col gap-2">
              <button
                type="submit"
                disabled={busy || !can.create}
                className="rounded-md bg-primary px-4 py-2.5 text-body font-semibold text-white hover:bg-primary/90 disabled:opacity-60"
              >
                {busy ? t('manualTicket.submitting') : t('manualTicket.submit')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => navigate(-1)}
                className="rounded-md border border-line-strong bg-background px-4 py-2.5 text-body font-medium text-ink hover:bg-surface-subtle disabled:opacity-60"
              >
                {t('manualTicket.cancel')}
              </button>
            </div>
          )}
        </aside>
      </form>
    </Page>
  )
}
